import { useMemo, useRef, useState } from 'react'
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Copy,
  Globe,
  Layout,
  Link2,
  Save,
  Sparkles,
  Wand2,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { slugify } from '@/lib/slugify'
import { cn } from '@/lib/utils'
import ProjectHubGantt from '@/components/hub/ProjectHubGantt'
import ProjectSecretsPanel from '@/components/hub/ProjectSecretsPanel'
import HubDisplayAssetsPanel from '@/components/hub/HubDisplayAssetsPanel'
import { emptyDisplayAssets, normalizeDisplayAssets } from '@/lib/projectDisplay'

const CATEGORIES = [
  { value: 'typology', label: 'Typology' },
  { value: 'material-index', label: 'Material Index' },
  { value: 'standalone', label: 'Standalone' },
  { value: 'internal-tool', label: 'Internal tool' },
  { value: 'other', label: 'Other' },
]

const TECH_STACK_PRESETS = [
  'React',
  'Vite',
  'Supabase',
  'Cloudflare Workers',
  'Tailwind CSS',
  'TypeScript',
  'Node.js',
  'Postgres',
]

const PHASE_TEMPLATES = [
  { key: 'ideate', title: 'Ideate' },
  { key: 'develop', title: 'Develop' },
  { key: 'test', title: 'Test' },
  { key: 'production', title: 'Production' },
]

/** Bare host or path OK: material-index.com → https://material-index.com */
function normalizeLiveSiteUrl(raw) {
  const s = String(raw || '').trim()
  if (!s) return ''
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return s
  return `https://${s.replace(/^\/+/, '')}`
}

const VISION_PRESETS = [
  'MVP',
  'Design system',
  'Automation',
  'Internal tool',
  'Experiment',
  'Revenue',
  'Audience growth',
  'Knowledge base',
]

function parseTechStack(value) {
  if (Array.isArray(value)) return value.filter(Boolean)
  return String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function parseVisionTags(value) {
  return String(value || '')
    .split(/[,\n|]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function formatIsoDay(date) {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseIsoDay(iso) {
  if (!iso || typeof iso !== 'string') return null
  const d = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

function addDays(iso, days) {
  const d = parseIsoDay(iso)
  if (!d) return iso
  d.setUTCDate(d.getUTCDate() + days)
  return formatIsoDay(d)
}

function dayDiffInclusive(startIso, endIso) {
  const s = parseIsoDay(startIso)
  const e = parseIsoDay(endIso)
  if (!s || !e) return 0
  return Math.floor((e.getTime() - s.getTime()) / 86400000) + 1
}

function normalizePhase(raw, idx) {
  const base = PHASE_TEMPLATES[idx] || { key: `phase-${idx}`, title: `Phase ${idx + 1}` }
  return {
    id:
      typeof raw?.id === 'string'
        ? raw.id
        : typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${base.key}-${Date.now()}-${idx}`,
    key: typeof raw?.key === 'string' ? raw.key : base.key,
    title: typeof raw?.title === 'string' && raw.title.trim() ? raw.title : base.title,
    start: typeof raw?.start === 'string' ? raw.start : '',
    end: typeof raw?.end === 'string' ? raw.end : '',
    status: raw?.status === 'completed' ? 'completed' : 'todo',
    completed_at: typeof raw?.completed_at === 'string' ? raw.completed_at : '',
  }
}

function generatePhases(startIso, deadlineIso) {
  const totalDays = dayDiffInclusive(startIso, deadlineIso)
  if (totalDays < 1) return []
  const n = PHASE_TEMPLATES.length
  const base = Math.floor(totalDays / n)
  const remainder = totalDays % n
  let cursor = startIso
  return PHASE_TEMPLATES.map((p, i) => {
    const span = Math.max(1, base + (i < remainder ? 1 : 0))
    const end = addDays(cursor, span - 1)
    const out = normalizePhase({ key: p.key, title: p.title, start: cursor, end }, i)
    cursor = addDays(end, 1)
    return out
  })
}

function normalizeSchedule(s) {
  const nowIso = formatIsoDay(new Date())
  if (!s || typeof s !== 'object') return { deadline: '', start_date: nowIso, phases: [] }
  const phases = Array.isArray(s.phases) ? s.phases.map(normalizePhase) : []
  return {
    deadline: typeof s.deadline === 'string' ? s.deadline : '',
    start_date: typeof s.start_date === 'string' && s.start_date ? s.start_date : nowIso,
    phases,
  }
}

function rebalanceSchedule(schedule, nextDeadline) {
  const sch = normalizeSchedule(schedule)
  const deadline = nextDeadline || sch.deadline
  if (!deadline) return { ...sch, deadline, phases: [] }
  const start = sch.start_date || formatIsoDay(new Date())
  const generated = generatePhases(start, deadline)
  if (!generated.length) return { ...sch, deadline, phases: [] }
  const existingByKey = new Map(sch.phases.map((p) => [p.key, p]))
  let completedCursor = start
  let firstOpenIndex = 0
  const merged = generated.map((g, idx) => {
    const ex = existingByKey.get(g.key)
    const isCompleted = ex?.status === 'completed'
    if (!isCompleted) return { ...g, status: 'todo', completed_at: '' }
    const completedAt = ex?.completed_at || g.end
    const safeEnd = dayDiffInclusive(completedCursor, deadline) > 0 ? completedAt : deadline
    const phase = {
      ...g,
      start: completedCursor,
      end: safeEnd,
      status: 'completed',
      completed_at: completedAt,
    }
    completedCursor = addDays(phase.end, 1)
    firstOpenIndex = idx + 1
    return phase
  })
  if (firstOpenIndex < merged.length) {
    const remainingTemplates = PHASE_TEMPLATES.slice(firstOpenIndex)
    const remainingDays = dayDiffInclusive(completedCursor, deadline)
    const base = Math.max(1, Math.floor(Math.max(remainingDays, remainingTemplates.length) / remainingTemplates.length))
    const rem = Math.max(remainingDays, remainingTemplates.length) % remainingTemplates.length
    let cursor = completedCursor
    for (let i = 0; i < remainingTemplates.length; i++) {
      const idx = firstOpenIndex + i
      const span = base + (i < rem ? 1 : 0)
      const end = addDays(cursor, span - 1)
      merged[idx] = {
        ...merged[idx],
        start: cursor,
        end: i === remainingTemplates.length - 1 ? deadline : end,
        status: 'todo',
        completed_at: '',
      }
      cursor = addDays(merged[idx].end, 1)
    }
  }
  return { deadline, start_date: start, phases: merged }
}

function emptyForm() {
  return {
    name: '',
    slug: '',
    description: '',
    category: 'standalone',
    status: 'idea',
    priority: 3,
    vision: '',
    tech_stack: '',
    env_template: '',
    link_website: '',
    link_github: '',
    link_figma: '',
    link_vercel: '',
    link_docs: '',
    published: false,
    share_token: '',
    schedule: { deadline: '', start_date: formatIsoDay(new Date()), phases: [] },
  }
}

export function projectRowToForm(row) {
  const links = row.external_links && typeof row.external_links === 'object' ? row.external_links : {}
  return {
    name: row.name || '',
    slug: row.slug || '',
    description: row.description || '',
    category: row.category || 'standalone',
    status: row.status || 'idea',
    priority: Number(row.priority) || 3,
    vision: row.vision || '',
    tech_stack: Array.isArray(row.tech_stack) ? row.tech_stack.join(', ') : '',
    env_template: row.env_template || '',
    link_website: links.website || '',
    link_github: links.github || '',
    link_figma: links.figma || '',
    link_vercel: links.vercel || '',
    link_docs: links.docs || '',
    published: Boolean(row.published),
    share_token: row.share_token || '',
    schedule: normalizeSchedule(row.schedule),
    display_assets: normalizeDisplayAssets(row.display_assets),
  }
}

function WizardShell({ step, children, title, subtitle }) {
  return (
    <div className="mx-auto max-w-md px-2 py-4 sm:py-8">
      <div className="mb-10 text-center">
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">New project</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{title}</h2>
        {subtitle ? <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{subtitle}</p> : null}
        <div className="mt-6 flex justify-center gap-1.5">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={cn('h-1 w-8 rounded-full transition-colors', n <= step ? 'bg-primary' : 'bg-muted')}
              aria-hidden
            />
          ))}
        </div>
      </div>
      {children}
    </div>
  )
}

export default function ProjectEditor({ userId, existingId = null, initialForm = null, onSaved, isNew = false }) {
  const [form, setForm] = useState(() => initialForm || emptyForm())
  const [wizardStep, setWizardStep] = useState(1)
  const [pendingSecrets, setPendingSecrets] = useState([])
  const [pendingKey, setPendingKey] = useState('')
  const [pendingVal, setPendingVal] = useState('')
  const [saving, setSaving] = useState(false)
  const [refining, setRefining] = useState(false)
  const [error, setError] = useState('')
  const [showNameDetails, setShowNameDetails] = useState(false)
  const [techInput, setTechInput] = useState('')
  const [visionInput, setVisionInput] = useState('')
  const [toast, setToast] = useState(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const toastTimerRef = useRef(0)

  const canGenerateSlug = useMemo(() => Boolean(form.name?.trim()), [form.name])
  const refineWorkerUrl = import.meta.env.VITE_AI_REFINE_URL?.trim()

  const showToast = (message, variant = 'success') => {
    window.clearTimeout(toastTimerRef.current)
    setToast({ message, variant })
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3800)
  }

  const mergeFormFromAi = (partial) => {
    setForm((prev) => ({
      ...prev,
      ...partial,
      tech_stack:
        partial.tech_stack != null
          ? Array.isArray(partial.tech_stack)
            ? partial.tech_stack.join(', ')
            : String(partial.tech_stack)
          : prev.tech_stack,
    }))
  }

  const techItems = useMemo(() => parseTechStack(form.tech_stack), [form.tech_stack])
  const visionTags = useMemo(() => parseVisionTags(form.vision), [form.vision])

  const setTechItems = (items) => {
    const unique = Array.from(new Set(items.map((x) => String(x).trim()).filter(Boolean)))
    setForm((f) => ({ ...f, tech_stack: unique.join(', ') }))
  }

  const toggleTechPreset = (label) => {
    const exists = techItems.some((x) => x.toLowerCase() === label.toLowerCase())
    if (exists) setTechItems(techItems.filter((x) => x.toLowerCase() !== label.toLowerCase()))
    else setTechItems([...techItems, label])
  }

  const addCustomTechTag = (value) => {
    const tag = String(value || '').trim()
    if (!tag) return
    if (techItems.some((x) => x.toLowerCase() === tag.toLowerCase())) return
    setTechItems([...techItems, tag])
  }

  const setVisionTags = (items) => {
    const unique = Array.from(new Set(items.map((x) => String(x).trim()).filter(Boolean)))
    setForm((f) => ({ ...f, vision: unique.join(', ') }))
  }

  const toggleVisionPreset = (label) => {
    const exists = visionTags.some((x) => x.toLowerCase() === label.toLowerCase())
    if (exists) setVisionTags(visionTags.filter((x) => x.toLowerCase() !== label.toLowerCase()))
    else setVisionTags([...visionTags, label])
  }

  const addCustomVisionTag = (value) => {
    const tag = String(value || '').trim()
    if (!tag) return
    if (visionTags.some((x) => x.toLowerCase() === tag.toLowerCase())) return
    setVisionTags([...visionTags, tag])
  }

  const handleRefineAi = async () => {
    const base = refineWorkerUrl
    if (!base) {
      showToast(
        'Add VITE_AI_REFINE_URL to .env (Worker base URL, no trailing slash), restart npm run dev, and redeploy Pages with the same var for production.',
        'info'
      )
      return
    }
    if (!form.vision?.trim()) {
      showToast('Add some vision / context text first.', 'info')
      return
    }
    setError('')
    setRefining(true)
    try {
      const secret = import.meta.env.VITE_AI_REFINE_SECRET
      const res = await fetch(base, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(secret ? { 'X-Refine-Secret': secret } : {}),
        },
        body: JSON.stringify({ vision: form.vision, name: form.name, slug: form.slug || undefined }),
      })
      const raw = await res.text()
      let data
      try {
        data = JSON.parse(raw)
      } catch {
        throw new Error(raw || `HTTP ${res.status}`)
      }
      if (!res.ok) throw new Error(data.error || data.detail || raw || `HTTP ${res.status}`)
      if (data.tech_stack) mergeFormFromAi({ tech_stack: data.tech_stack })
      if (data.env_template) mergeFormFromAi({ env_template: data.env_template })
      if (data.description) mergeFormFromAi({ description: data.description })
      showToast('AI suggestions applied.')
    } catch (e) {
      setError(e.message || 'Refine failed')
    } finally {
      setRefining(false)
    }
  }

  const buildPayload = (slug) => {
    const tech_stack = parseTechStack(form.tech_stack)
    const liveSite = normalizeLiveSiteUrl(form.link_website)
    const external_links = {
      ...(liveSite && { website: liveSite }),
      ...(form.link_github && { github: form.link_github.trim() }),
      ...(form.link_figma && { figma: form.link_figma.trim() }),
      ...(form.link_vercel && { vercel: form.link_vercel.trim() }),
      ...(form.link_docs && { docs: form.link_docs.trim() }),
    }
    return {
      user_id: userId,
      name: form.name.trim(),
      slug,
      description: form.description.trim() || null,
      category: form.category || null,
      status: form.status,
      priority: Math.min(5, Math.max(1, Number(form.priority) || 3)),
      vision: form.vision.trim() || null,
      tech_stack,
      env_template: form.env_template.trim() || null,
      external_links,
      published: Boolean(form.published),
      share_token: form.share_token || null,
      schedule: form.schedule || {},
      display_assets: normalizeDisplayAssets(form.display_assets || {}),
    }
  }

  const handleSave = async () => {
    if (!userId) {
      setError('Not signed in.')
      return
    }
    const slug = (form.slug || slugify(form.name)).trim()
    if (!slug) {
      setError('Slug or name required.')
      return
    }

    const payload = buildPayload(slug)

    setSaving(true)
    setError('')

    try {
      if (existingId) {
        const { error: upErr } = await supabase.from('hub_projects').update(payload).eq('id', existingId).eq('user_id', userId)
        if (upErr) throw upErr
      } else {
        const { error: insErr } = await supabase.from('hub_projects').insert(payload)
        if (insErr) throw insErr
      }
      showToast('Project saved.')
      onSaved?.({ slug, ...payload })
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleCreateNew = async () => {
    if (!userId) {
      setError('Not signed in.')
      return
    }
    const slug = slugify(form.name).trim()
    if (!slug) {
      setError('Name required.')
      return
    }
    const desc = form.description.trim()
    const liveSite = normalizeLiveSiteUrl(form.link_website)
    const payload = {
      user_id: userId,
      name: form.name.trim(),
      slug,
      description: desc || null,
      category: form.category || null,
      status: form.status,
      priority: Math.min(5, Math.max(1, Number(form.priority) || 3)),
      vision: null,
      tech_stack: [],
      env_template: null,
      external_links: liveSite ? { website: liveSite } : {},
      published: false,
      share_token: null,
      schedule: {},
      display_assets: {},
    }

    setSaving(true)
    setError('')
    try {
      const { data: row, error: insErr } = await supabase.from('hub_projects').insert(payload).select('id').single()
      if (insErr) throw insErr
      const pid = row?.id
      if (pid && pendingSecrets.length) {
        const { error: secErr } = await supabase.from('hub_project_secrets').insert(
          pendingSecrets.map((p) => ({
            project_id: pid,
            key: p.key,
            value: p.value,
          }))
        )
        if (secErr) throw secErr
      }
      showToast('Project created.')
      setPendingSecrets([])
      onSaved?.({ slug })
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  const addPendingSecret = () => {
    const key = pendingKey.trim().toUpperCase().replace(/\s+/g, '_')
    if (!key || !pendingVal) return
    setPendingSecrets((prev) => {
      const next = prev.filter((p) => p.key !== key)
      next.push({ key, value: pendingVal })
      return next
    })
    setPendingKey('')
    setPendingVal('')
  }

  const removePendingSecret = (key) => {
    setPendingSecrets((prev) => prev.filter((p) => p.key !== key))
  }

  const copyShareLink = async () => {
    if (!form.share_token) return
    const url = `${window.location.origin}/share/${form.share_token}`
    try {
      await navigator.clipboard.writeText(url)
      showToast('Link copied.')
    } catch {
      showToast('Could not copy.', 'error')
    }
  }

  const enableShareLink = async () => {
    if (!existingId) return
    const token = crypto.randomUUID()
    setSaving(true)
    setError('')
    try {
      const { error: upErr } = await supabase
        .from('hub_projects')
        .update({ share_token: token })
        .eq('id', existingId)
        .eq('user_id', userId)
      if (upErr) throw upErr
      setForm((f) => ({ ...f, share_token: token }))
      showToast('Sharing enabled.')
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  const revokeShareLink = async () => {
    if (!existingId) return
    setSaving(true)
    setError('')
    try {
      const { error: upErr } = await supabase
        .from('hub_projects')
        .update({ share_token: null })
        .eq('id', existingId)
        .eq('user_id', userId)
      if (upErr) throw upErr
      setForm((f) => ({ ...f, share_token: '' }))
      showToast('Share link removed.')
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  const togglePublished = async () => {
    if (!existingId) return
    const next = !form.published
    setSaving(true)
    setError('')
    try {
      const { error: upErr } = await supabase
        .from('hub_projects')
        .update({ published: next })
        .eq('id', existingId)
        .eq('user_id', userId)
      if (upErr) throw upErr
      setForm((f) => ({ ...f, published: next }))
      showToast(next ? 'Published on the site.' : 'Unpublished.')
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  const applyDeadline = (deadlineIso) => {
    setForm((f) => {
      const nextSchedule = rebalanceSchedule(f.schedule, deadlineIso)
      return {
        ...f,
        schedule: nextSchedule,
      }
    })
  }

  const togglePhaseCompleted = (phaseKey) => {
    setForm((f) => {
      const sch = normalizeSchedule(f.schedule)
      const toggled = sch.phases.map((p) => {
        if (p.key !== phaseKey) return p
        const nextCompleted = p.status !== 'completed'
        return {
          ...p,
          status: nextCompleted ? 'completed' : 'todo',
          completed_at: nextCompleted ? formatIsoDay(new Date()) : '',
        }
      })
      return {
        ...f,
        schedule: rebalanceSchedule({ ...sch, phases: toggled }, sch.deadline),
      }
    })
  }

  if (isNew && !existingId) {
    return (
      <div className="rounded-2xl border border-border/80 bg-card text-card-foreground shadow-sm overflow-hidden">
        {toast && (
          <div
            className={cn(
              'fixed bottom-6 left-1/2 z-[100] -translate-x-1/2 rounded-xl border px-4 py-2 text-sm shadow-lg',
              toast.variant === 'success' && 'border-border bg-background text-foreground',
              toast.variant === 'info' && 'border-sky-500/40 bg-sky-950/90 text-sky-100',
              toast.variant === 'error' && 'border-red-500/40 bg-red-950/90 text-red-100'
            )}
            role="status"
          >
            {toast.message}
          </div>
        )}

        {wizardStep === 1 && (
          <WizardShell step={1} title="Name" subtitle="What are you building?">
            <input
              autoFocus
              className="w-full rounded-2xl border border-input bg-background px-4 py-3.5 text-base shadow-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Project name"
            />
            <button
              type="button"
              disabled={!form.name?.trim()}
              onClick={() => setWizardStep(2)}
              className="mt-8 w-full rounded-2xl bg-primary py-3.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-40"
            >
              Continue
            </button>
          </WizardShell>
        )}

        {wizardStep === 2 && (
          <WizardShell step={2} title="Details" subtitle="Category, stage, and a short description.">
            <div className="space-y-5">
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="w-cat">
                  Category
                </label>
                <select
                  id="w-cat"
                  className="mt-1.5 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="w-stage">
                  Stage
                </label>
                <select
                  id="w-stage"
                  className="mt-1.5 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="idea">Idea</option>
                  <option value="active">Active</option>
                  <option value="archive">Archive</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="w-desc">
                  Context / description
                </label>
                <textarea
                  id="w-desc"
                  className="mt-1.5 w-full min-h-[120px] rounded-2xl border border-input bg-background px-4 py-3 text-sm leading-relaxed"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="A sentence or two."
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="w-site">
                  Live site (optional)
                </label>
                <input
                  id="w-site"
                  type="text"
                  className="mt-1.5 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                  value={form.link_website}
                  onChange={(e) => setForm({ ...form, link_website: e.target.value })}
                  placeholder="material-index.com or https://…"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <button
                type="button"
                onClick={() => setWizardStep(1)}
                className="flex-1 rounded-2xl border border-border py-3.5 text-sm font-medium text-muted-foreground hover:bg-muted/40"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setWizardStep(3)}
                className="flex-1 rounded-2xl bg-primary py-3.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                Continue
              </button>
            </div>
          </WizardShell>
        )}

        {wizardStep === 3 && (
          <WizardShell
            step={3}
            title="Secrets"
            subtitle="Stored securely; they will not be shown again in this form."
          >
            <div className="space-y-3">
              {pendingSecrets.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-muted/10 px-3 py-2"
                >
                  <div>
                    <p className="font-mono text-xs font-medium">{p.key}</p>
                    <p className="text-[11px] text-muted-foreground">••••••••••••</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePendingSecret(p.key)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Remove
                  </button>
                </div>
              ))}
              {pendingSecrets.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-2">Optional — skip if none yet.</p>
              ) : null}
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <div>
                <label className="text-[11px] font-medium text-muted-foreground">Variable</label>
                <input
                  className="mt-1 w-full rounded-2xl border border-input bg-background px-3 py-2.5 font-mono text-xs"
                  value={pendingKey}
                  onChange={(e) => setPendingKey(e.target.value)}
                  placeholder="API_KEY"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-muted-foreground">Value</label>
                <input
                  type="password"
                  className="mt-1 w-full rounded-2xl border border-input bg-background px-3 py-2.5 font-mono text-xs"
                  value={pendingVal}
                  onChange={(e) => setPendingVal(e.target.value)}
                  placeholder="Paste once"
                  autoComplete="new-password"
                />
              </div>
              <button
                type="button"
                onClick={addPendingSecret}
                disabled={!pendingKey.trim() || !pendingVal}
                className="h-10 rounded-2xl border border-border bg-background px-3 text-xs font-medium hover:bg-muted disabled:opacity-40"
              >
                Add
              </button>
            </div>

            <button
              type="button"
              disabled={saving}
              onClick={handleCreateNew}
              className="mt-10 w-full rounded-2xl bg-primary py-3.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create project'}
            </button>
            <button
              type="button"
              onClick={() => setWizardStep(2)}
              className="mt-3 w-full rounded-2xl py-3 text-sm font-medium text-muted-foreground hover:bg-muted/30"
            >
              Back
            </button>
          </WizardShell>
        )}

        {error ? <p className="border-t border-border px-6 py-3 text-sm text-red-400">{error}</p> : null}
      </div>
    )
  }

  const sch = normalizeSchedule(form.schedule)

  return (
    <div className="rounded-2xl border border-border bg-card text-card-foreground shadow-xl overflow-hidden">
      {toast && (
        <div
          className={cn(
            'fixed bottom-6 left-1/2 z-[100] -translate-x-1/2 rounded-lg border px-4 py-2 text-sm shadow-lg',
            toast.variant === 'success' && 'border-border bg-background text-foreground',
            toast.variant === 'info' && 'border-sky-500/40 bg-sky-950/90 text-sky-100',
            toast.variant === 'error' && 'border-red-500/40 bg-red-950/90 text-red-100'
          )}
          role="status"
        >
          {toast.message}
        </div>
      )}

      <div className="flex flex-col gap-4 border-b border-border bg-muted/20 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
          <Layout className="h-5 w-5 text-muted-foreground" aria-hidden />
          Project
        </h2>
        <div className="flex flex-wrap gap-2">
          {showNameDetails ? (
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, slug: slugify(f.name) }))}
              disabled={!canGenerateSlug}
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              <Wand2 className="h-3.5 w-3.5" />
              Slug from name
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="p-6 space-y-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="p-name">
                  Name
                </label>
                <button
                  type="button"
                  onClick={() => setShowNameDetails((v) => !v)}
                  className="text-[11px] text-muted-foreground hover:text-foreground"
                >
                  {showNameDetails ? 'Hide details' : 'Name details'}
                </button>
              </div>
              <input
                id="p-name"
                className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Project name"
              />
            </div>
            {showNameDetails ? (
              <div>
                <label className="text-xs font-medium text-muted-foreground" htmlFor="p-slug">
                  Slug
                </label>
                <input
                  id="p-slug"
                  className="mt-1 w-full font-mono text-sm rounded-2xl border border-input bg-background px-4 py-3"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="material-index"
                />
              </div>
            ) : null}
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="p-cat">
                Category
              </label>
              <select
                id="p-cat"
                className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="p-desc">
                Description
              </label>
              <textarea
                id="p-desc"
                className="mt-1 w-full min-h-[88px] rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short summary"
              />
            </div>
            {/* Vision & context intentionally hidden for now. */}
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="p-status">
                Stage
              </label>
              <select
                id="p-status"
                className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="idea">Idea / brainstorm</option>
                <option value="active">Active</option>
                <option value="archive">Archive</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="p-priority">
                Priority (1 = low, 5 = high)
              </label>
              <select
                id="p-priority"
                className="mt-1 w-full rounded-2xl border border-input bg-background px-4 py-3 text-sm"
                value={String(form.priority)}
                onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={String(n)}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-2xl border border-dashed border-border bg-muted/10 p-4">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Non-secret .env template
              </label>
              <p className="mt-1 text-[11px] text-muted-foreground leading-relaxed">
                Comments and public keys only. Sensitive values belong in Secrets below.
              </p>
              <textarea
                className="mt-3 w-full min-h-[100px] resize-y bg-transparent font-mono text-xs leading-relaxed focus:outline-none rounded-xl border border-transparent focus:border-input px-1"
                placeholder={'# VITE_PUBLIC_…=\n'}
                value={form.env_template}
                onChange={(e) => setForm({ ...form, env_template: e.target.value })}
              />
            </div>

            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className="flex w-full items-center justify-between rounded-2xl border border-border px-4 py-3 text-left text-sm font-medium"
            >
              <span>Advanced</span>
              {advancedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>

            {advancedOpen ? (
              <div className="space-y-3 rounded-2xl border border-border/60 bg-muted/5 p-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">Tech stack</label>
                  <div className="flex flex-wrap gap-2">
                    {TECH_STACK_PRESETS.map((preset) => {
                      const active = techItems.some((x) => x.toLowerCase() === preset.toLowerCase())
                      return (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => toggleTechPreset(preset)}
                          className={cn(
                            'rounded-full border px-2.5 py-1 text-xs transition-colors',
                            active
                              ? 'border-primary bg-primary/15 text-foreground'
                              : 'border-border bg-background text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {preset}
                        </button>
                      )
                    })}
                  </div>
                  <div className="flex gap-2">
                    <input
                      id="p-tech"
                      className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                      value={techInput}
                      onChange={(e) => setTechInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addCustomTechTag(techInput)
                          setTechInput('')
                        }
                      }}
                      placeholder="Type custom tag and press Enter"
                    />
                    <button
                      type="button"
                      className="rounded-xl border border-border px-3 py-2 text-xs"
                      onClick={() => {
                        addCustomTechTag(techInput)
                        setTechInput('')
                      }}
                    >
                      Add
                    </button>
                  </div>
                  {techItems.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {techItems.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setTechItems(techItems.filter((x) => x !== t))}
                          className="rounded-full border border-border/80 bg-muted/30 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                          title="Remove tag"
                        >
                          {t} ×
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                {[
                  ['link_website', 'Live site / project URL', 'text', 'material-index.com or https://…'],
                  ['link_github', 'GitHub URL', 'url', ''],
                  ['link_figma', 'Figma URL', 'url', ''],
                  ['link_vercel', 'Vercel / deploy URL', 'url', ''],
                  ['link_docs', 'Docs URL', 'url', ''],
                ].map(([key, label, inputType, placeholder]) => (
                  <div key={key}>
                    <label className="text-[11px] text-muted-foreground" htmlFor={key}>
                      {label}
                    </label>
                    <input
                      id={key}
                      type={inputType}
                      className="mt-0.5 w-full rounded-xl border border-input bg-background px-2 py-1.5 text-xs"
                      value={form[key]}
                      placeholder={placeholder || undefined}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    />
                  </div>
                ))}
                {!refineWorkerUrl ? (
                  <p className="rounded-xl border border-amber-500/25 bg-amber-950/20 px-3 py-2 text-[11px] leading-relaxed text-amber-100/90">
                    Refine needs the Cloudflare Worker URL in your environment: set{' '}
                    <code className="rounded bg-black/30 px-1 font-mono text-[10px]">VITE_AI_REFINE_URL=https://…workers.dev</code>{' '}
                    in <code className="font-mono text-[10px]">.env</code>, then restart the dev server. Deploy the worker
                    if needed: <code className="font-mono text-[10px]">npm run worker:refine:deploy</code>. See{' '}
                    <code className="font-mono text-[10px]">workers/hub-refine/README.md</code>.
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={handleRefineAi}
                  disabled={refining || !refineWorkerUrl}
                  className={cn(
                    'flex w-full items-center justify-center gap-2 rounded-xl border border-violet-500/35 bg-violet-950/30 py-2.5 text-sm text-violet-200 hover:bg-violet-950/50 disabled:opacity-50'
                  )}
                >
                  <Sparkles className="h-4 w-4" />
                  {refining ? 'Refining…' : 'Refine with AI (Worker)'}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {existingId ? (
          <>
            <div className="rounded-2xl border border-border/80 bg-muted/5 p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
                <CalendarClock className="h-4 w-4 text-muted-foreground" aria-hidden />
                Schedule &amp; phases
              </div>
              <p className="text-xs text-muted-foreground">
                Pick a deadline once. Phases are auto-generated and rebalanced as phases are completed.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground" htmlFor="p-start-date">
                    Start date
                  </label>
                  <input
                    id="p-start-date"
                    type="date"
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                    value={sch.start_date || ''}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        schedule: rebalanceSchedule({ ...normalizeSchedule(f.schedule), start_date: e.target.value }, sch.deadline),
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground" htmlFor="p-deadline">
                    Deadline
                  </label>
                  <input
                    id="p-deadline"
                    className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
                    type="date"
                    value={sch.deadline || ''}
                    onChange={(e) => applyDeadline(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-muted-foreground">Auto phases</span>
                  <button
                    type="button"
                    onClick={() => applyDeadline(sch.deadline)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Regenerate
                  </button>
                </div>
                {sch.phases.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Set a deadline to generate phases.</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Click a phase block in the chart to toggle completion.
                  </p>
                )}
              </div>
              <ProjectHubGantt schedule={form.schedule} onTogglePhase={togglePhaseCompleted} />
            </div>

            <HubDisplayAssetsPanel projectId={existingId} form={form} setForm={setForm} />

            <div className="grid gap-6 lg:grid-cols-2">
              <ProjectSecretsPanel projectId={existingId} slug={form.slug} />
              <div className="rounded-2xl border border-border/80 bg-muted/5 p-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
                  <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
                  Publish
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Published projects appear in the public &ldquo;Selected Projects&rdquo; section alongside portfolio
                  entries.
                </p>
                <button
                  type="button"
                  onClick={togglePublished}
                  disabled={saving}
                  className={cn(
                    'w-full rounded-xl border px-4 py-3 text-sm font-medium transition-colors',
                    form.published
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-background hover:bg-muted/40'
                  )}
                >
                  {form.published ? 'Published on christianfiebig.de' : 'Not published — click to publish'}
                </button>

                <div className="flex items-center gap-2 text-sm font-semibold tracking-tight pt-2 border-t border-border/60">
                  <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                  Share link
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Read-only page for collaborators; never includes secrets or env files.
                </p>
                {form.share_token ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        readOnly
                        className="min-w-0 flex-1 rounded-xl border border-input bg-muted/30 px-3 py-2 font-mono text-[11px]"
                        value={`${typeof window !== 'undefined' ? window.location.origin : ''}/share/${form.share_token}`}
                      />
                      <button
                        type="button"
                        onClick={copyShareLink}
                        className="shrink-0 rounded-xl border border-border px-3 py-2 hover:bg-muted/50"
                        aria-label="Copy link"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={revokeShareLink}
                      disabled={saving}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      Disable sharing
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={enableShareLink}
                    disabled={saving}
                    className="w-full rounded-xl border border-border bg-background py-2.5 text-sm font-medium hover:bg-muted/40 disabled:opacity-50"
                  >
                    Create share link
                  </button>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>

      {error ? <p className="border-t border-border px-6 py-3 text-sm text-red-400">{error}</p> : null}
    </div>
  )
}
